import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  FilterOutlined,
  GiftOutlined,
  TeamOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import "./live-series-page.scss";
import heroImage from "../../assets/live/live-series-hero.png";
import httpclient from "../../service/httpclient";
import LiveGroupBuyPosterModal from "./LiveGroupBuyPosterModal";

const filterOptions = [
  { key: "all", label: "全部" },
  { key: "free", label: "免费" },
  { key: "paid", label: "付费" },
  { key: "upcoming", label: "即将开始" },
  { key: "ended", label: "已结束" },
];

function getFilteredCourses(activeFilter, courses) {
  if (activeFilter === "all") return courses;
  if (activeFilter === "free" || activeFilter === "paid") {
    return courses.filter((course) => course.priceType === activeFilter);
  }
  return courses.filter((course) => course.status === activeFilter);
}

function getFilterCount(key, courses) {
  if (key === "all") return courses.length;
  if (key === "free" || key === "paid") {
    return courses.filter((course) => course.priceType === key).length;
  }
  return null;
}

function mapCatalogToCourses(catalog) {
  return catalog
    .flatMap((series) =>
      (series.streams || []).map((stream) => {
        const price = stream.price ?? series.price;
        const isPaid =
          stream.purchaseMode === "voucher_only" ||
          series.purchaseMode === "voucher_only" ||
          (price !== null && Number(price) > 0);
        const hasSchedule = !!stream.scheduledAt;
        const scheduledAt = hasSchedule
          ? new Date(stream.scheduledAt).toLocaleString("zh-CN", {
              month: "long",
              day: "numeric",
              weekday: "short",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "直播讲解将视今年秋招时间暂定";
        const status =
          stream.status === "ENDED"
            ? "ended"
            : stream.status === "LIVE"
            ? "live"
            : "upcoming";
        const accent =
          status === "live" ? "#e74c3c" : status === "ended" ? "#1677ff" : "#18b985";

        return {
          id: stream.id,
          title: stream.title,
          priceType: isPaid ? "paid" : "free",
          price,
          status,
          date: scheduledAt,
          hasSchedule,
          duration: stream.duration ? `${stream.duration} 分钟` : "时长待定",
          content: series.title,
          description: stream.description || series.description || "",
          accent,
          hasAccess: stream.hasAccess || false,
        };
      })
    )
    .sort((a, b) => a.id - b.id);
}

function CourseCard({ course }) {
  const [showGroupBuyModal, setShowGroupBuyModal] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descriptionRef = useRef(null);
  const isFree = course.priceType === "free";
  const statusLabel =
    course.status === "live"
      ? "直播中"
      : course.status === "ended"
      ? "已结束"
      : "即将开始";

  useEffect(() => {
    const checkDescriptionOverflow = () => {
      if (!descriptionRef.current) return;

      setIsTruncated(
        descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight + 1
      );
    };

    checkDescriptionOverflow();
    window.addEventListener("resize", checkDescriptionOverflow);

    return () => {
      window.removeEventListener("resize", checkDescriptionOverflow);
    };
  }, [course.description]);

  const renderActionButtons = () => {
    if (!course.hasSchedule) {
      return (
        <Tooltip title="具体上线时间将视公司秋招笔试时间而定，欢迎大家持续关注ICDesign直播栏目！">
          <div className="live-course-action disabled">
            敬请期待
          </div>
        </Tooltip>
      );
    }

    if (course.hasAccess) {
      const buttonText =
        course.status === "live"
          ? "进入直播间"
          : course.status === "ended"
          ? "直播已结束"
          : "已购买，等待开播";
      const buttonClass =
        course.status === "live"
          ? "live-course-action enter-live"
          : course.status === "ended"
          ? "live-course-action ended"
          : "live-course-action waiting";

      return (
        <Link
          to={`/streaming/stream/${course.id}`}
          className={buttonClass}
        >
          {buttonText}
        </Link>
      );
    }

    if (isFree) {
      return (
        <Link
          to={`/streaming/stream/${course.id}`}
          className="live-course-action free-activate"
        >
          免费激活
        </Link>
      );
    }

    if (course.status === "ended") {
      return (
        <Link
          to={`/checkout/streaming_stream/${course.id}`}
          className="live-course-action buy"
        >
          购买
        </Link>
      );
    }

    return (
      <>
        <button
          type="button"
          className="live-course-action group"
          onClick={() => setShowGroupBuyModal(true)}
        >
          拼团
        </button>
        <Link
          to={`/checkout/streaming_stream/${course.id}`}
          className="live-course-action buy"
        >
          购买
        </Link>
      </>
    );
  };

  return (
    <>
      <article
        className="live-course-card"
        style={{ "--course-accent": course.accent }}
      >
        <div className="live-course-card-header">
          <div className="live-course-title">
            {isFree && <GiftOutlined />}
            <span>{course.title}</span>
          </div>
          <span className="live-course-status">
            {statusLabel}
          </span>
        </div>

        <div className={`live-course-price ${isFree ? "free" : "paid"}`}>
          {isFree ? "免费公开课" : course.price ? `¥${course.price}` : "付费课程"}
        </div>

        <div className="live-course-meta">
          <div>
            <CalendarOutlined />
            <span>{course.date}</span>
          </div>
          <div>
            <ClockCircleOutlined />
            <span>{course.duration}</span>
          </div>
        </div>

        <Tooltip overlay={<div style={{ whiteSpace: "pre-line", maxWidth: "400px" }}>{course.description}</div>}>
          <div className="live-course-description-wrap">
            <p ref={descriptionRef} className="live-course-description">
              {course.description}
            </p>
            {isTruncated && (
              <div className="live-course-description-ellipsis">......</div>
            )}
          </div>
        </Tooltip>

        <div className="live-course-actions">
          {renderActionButtons()}
        </div>
      </article>

      {showGroupBuyModal && (
        <LiveGroupBuyPosterModal
          posterUrl={
            ({
              24: "https://cdn.datongbei.com/icdesign-1783998925921fe5139d2a8ea.jpg",
              31: "https://cdn.datongbei.com/icdesign-17852951842757ehh9c20h2375.jpg",
              32: "https://cdn.datongbei.com/icdesign-1785414142549829b82bhh1g3h.jpg",
              34: "https://cdn.datongbei.com/icdesign-17860208079653hh1ag47c7582.jpg",
              35: "https://cdn.datongbei.com/icdesign-178641247503234bf4bfe1d444.jpg",
              36: "https://cdn.datongbei.com/icdesign-1786808495840agfc7e54ch231.jpg",
              37: "https://cdn.datongbei.com/icdesign-1787277707255e614d85a11284.jpg",
              38: "https://cdn.datongbei.com/icdesign-17875811447421a535a2eb392bb.jpg",
              39: "https://cdn.datongbei.com/icdesign-17880082862661db5b351a663b8.jpg",
              40: "https://cdn.datongbei.com/icdesign-1788446675239hb0367eh2bdba.jpg"
            })[course.id] || ""
          }
          onClose={() => setShowGroupBuyModal(false)}
        />
      )}
    </>
  );
}

function LiveSeriesPage() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    httpclient
      .getStreamingSeriesCatalog()
      .then((catalog) => setCourses(mapCatalogToCourses(catalog)))
      .catch(() => setError("直播课程加载失败，请稍后重试"))
      .finally(() => setLoading(false));
  }, []);

  const filteredCourses = useMemo(
    () => getFilteredCourses(activeFilter, courses),
    [activeFilter, courses]
  );

  return (
    <main className="live-series-page">
      <section
        className="live-series-hero"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="live-series-hero-inner">
          <div className="live-series-kicker">
            <span className="live-series-kicker-dot" />
            LIVE SERIES
          </div>
          <h1>IC 校招笔试真题直播精讲</h1>
          <p>
            覆盖联发科、紫光、德州仪器等 10-12 家头部芯片企业校招真题，
            从选择到解答，系统提升你的笔试通过率。
          </p>
          <div className="live-series-stats">
            <span>
              <CalendarOutlined />
              7月15日 - 8月30日
            </span>
            <span>
              <VideoCameraOutlined />
              共 {courses.length} 场直播
            </span>
            <span>
              <TeamOutlined />
              含 2 场免费公开课
            </span>
          </div>
        </div>
      </section>

      <section className="live-series-filter-bar">
        <div className="live-series-filter-inner">
          <FilterOutlined className="live-series-filter-icon" />
          {filterOptions.map((option) => {
            const count = getFilterCount(option.key, courses);
            return (
              <button
                key={option.key}
                type="button"
                className={`live-series-filter ${
                  activeFilter === option.key ? "active" : ""
                }`}
                onClick={() => setActiveFilter(option.key)}
              >
                {option.label}
                {count !== null && <span>{count}</span>}
              </button>
            );
          })}
        </div>
      </section>

      <section className="live-series-content">
        <h2>
          课程列表 <span>（{courses.length}场）</span>
        </h2>
        {loading ? (
          <div className="live-course-empty">直播课程加载中...</div>
        ) : error ? (
          <div className="live-course-empty">{error}</div>
        ) : filteredCourses.length ? (
          <div className="live-course-grid">
            {filteredCourses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        ) : (
          <div className="live-course-empty">暂无符合条件的课程</div>
        )}
      </section>
    </main>
  );
}

export default LiveSeriesPage;
